'use client';

import * as React from 'react';
import { Select as SelectPrimitive } from '@base-ui/react/select';
import { FaIcon } from '@micks-yard/icons/FaIcon';
import { faCheck } from '@micks-yard/icons/solid/faCheck';
import { faChevronDown } from '@micks-yard/icons/solid/faChevronDown';

import { cn } from '../../lib/utils';
import { Label } from '../label';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SimpleSelectProps {
  id?: string;
  value: string;
  onValueChange: (value: string) => void;
  options: readonly SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  label?: string;
  className?: string;
}

function SimpleSelect({
  id,
  value,
  onValueChange,
  options,
  placeholder = 'Select…',
  disabled = false,
  label,
  className,
}: SimpleSelectProps) {
  return (
    <div className={cn('flex w-full flex-col gap-2', className)}>
      {label ? <Label htmlFor={id}>{label}</Label> : null}
      <SelectPrimitive.Root
        items={options}
        value={value || null}
        onValueChange={(next) => {
          if (typeof next === 'string') onValueChange(next);
          else if (next == null) onValueChange('');
        }}
        disabled={disabled}>
        <SelectPrimitive.Trigger
          id={id}
          className={cn(
            'border-input bg-background text-foreground flex h-8 w-full items-center justify-between gap-2 rounded-lg border px-2.5 text-sm outline-none focus-visible:border-green-600 focus-visible:ring-3 focus-visible:ring-green-600/30 disabled:cursor-not-allowed disabled:opacity-50'
          )}>
          <SelectPrimitive.Value placeholder={placeholder} />
          <SelectPrimitive.Icon>
            <FaIcon icon={faChevronDown} className="text-muted-foreground size-3.5" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Positioner sideOffset={4} className="z-50 outline-none">
            <SelectPrimitive.Popup className="border-border bg-popover text-popover-foreground shadow-app-md max-h-72 min-w-[var(--anchor-width)] overflow-auto rounded-lg border p-1 outline-none">
              {options.map((option) => (
                <SelectPrimitive.Item
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  className="data-highlighted:bg-muted flex cursor-pointer items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-sm outline-none data-selected:font-medium">
                  <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                  <SelectPrimitive.ItemIndicator>
                    <FaIcon icon={faCheck} className="size-3.5 text-green-600" />
                  </SelectPrimitive.ItemIndicator>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Popup>
          </SelectPrimitive.Positioner>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </div>
  );
}

export { SimpleSelect };
